import type { Geometry, LineString, MultiPoint, Position } from "geojson";
import { z } from "zod";

import type {
  ImportGranularity,
  ImportProvider,
  ItineraryImportConfirmParams,
  ItineraryImportConfirmSegment,
  ItineraryImportConfirmWaypoint,
  ItineraryImportPreview,
  ItinerarySegment,
  ItineraryWaypoint,
  SegmentMode,
  WaypointMode,
} from "@mountayaapp/types/api/api";

import { buildGeometryCollection } from "../studio/geometry";
import type { ItineraryViewSource } from "../studio/itineraryView";
import {
  type LiveSegmentRouteAnalysis,
  type LiveSegmentRouteElevation,
  liveSegmentRouteAnalysisSchema,
  liveSegmentRouteElevationSchema,
  overlayRouteAnalysis,
} from "../studio/liveSegment";
import { segmentColorAt } from "../studio/segmentColors";
import { defaultSegmentName } from "../studio/segmentNaming";
import { waypointIconSchema } from "../studio/waypointIcons";

/**
 * importProviderIdSchema names the import sources the studio can ingest from: the
 * file formats a user uploads and the one online provider an external id resolves
 * against (`outdooractive`). It matches the wire ImportProvider. Zod-first because
 * the value selects which provider surface the import shell opens and is shared with
 * the open-import action, and the registry of provider surfaces keys off this id;
 * pinned to the wire type via `satisfies` so the two cannot drift.
 */
export const importProviderIdSchema = z.enum([
  "gpx",
  "geojson",
  "kml",
  "kmz",
  "fit",
  "tcx",
  "outdooractive",
]) satisfies z.ZodType<ImportProvider>;

/**
 * ImportProviderId names an import source, derived from importProviderIdSchema.
 */
export type ImportProviderId = z.infer<typeof importProviderIdSchema>;

/**
 * importInputKindSchema names how a provider's payload reaches the preview
 * endpoint: a `file` upload, an `external_id` lookup against an online provider
 * (outdooractive), or an `oauth` connection (reserved for providers that need a
 * linked account). Zod-first because the input surface a provider renders branches
 * on this kind.
 */
export const importInputKindSchema = z.enum(["file", "external_id", "oauth"]);

/**
 * ImportInputKind names how a provider's payload reaches preview, derived from
 * importInputKindSchema.
 */
export type ImportInputKind = z.infer<typeof importInputKindSchema>;

/**
 * importGranularitySchema is how the server should slice the source into
 * segments: `auto` lets the segmenter split on the source's own structure
 * (tracks, segments, large gaps), `single` collapses the whole source into one
 * segment. It matches the wire ImportGranularity. Zod-first because the value is
 * shared with the preview request and the import shell's granularity control;
 * pinned to the wire type via `satisfies` so the two cannot drift.
 */
export const importGranularitySchema = z.enum([
  "auto",
  "single",
]) satisfies z.ZodType<ImportGranularity>;

/**
 * importSegmentModeSchema is how a proposed segment's geometry is authored —
 * `directions` (a routed candidate) or `linestring` (a freehand line). It matches
 * the wire SegmentMode and is the chosen-mode vocabulary the import draft carries
 * per segment. Defined locally (rather than imported from the UI store) so the
 * import model stays a self-contained leaf; pinned to the wire SegmentMode via
 * `satisfies` so the two cannot drift.
 */
export const importSegmentModeSchema = z.enum([
  "directions",
  "linestring",
]) satisfies z.ZodType<SegmentMode>;

/**
 * importWaypointModeSchema is a proposed waypoint's mode — `poi` (the source
 * carried a mappable icon) or `point` (a plain marker). It matches the wire
 * WaypointMode and rides through the draft unchanged. Pinned to the wire
 * WaypointMode via `satisfies` so the two cannot drift.
 */
export const importWaypointModeSchema = z.enum(["poi", "point"]) satisfies z.ZodType<WaypointMode>;

/**
 * geometrySchema validates a value is a GeoJSON Geometry at the import boundary:
 * an object carrying a `type` discriminator. The full coordinate structure is
 * NOT re-validated — GeoJSON is structurally large and trusted in-process, the
 * same stance the segment edit session takes on its draft geometry — so this is
 * a shallow guard that rejects a non-geometry (a string, null, a bare object)
 * before it lands in the draft, then carries the value as the `@types/geojson`
 * Geometry the editor and the map read. Reused for both candidate geometries and
 * the waypoint point.
 */
const geometrySchema = z.custom<Geometry>(
  (value): value is Geometry =>
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string",
  { message: "expected a GeoJSON Geometry" },
);

/**
 * importCandidateSchema is one authored geometry the draft can render for a
 * segment: its authoring mode and the GeoJSON geometry, the server's optional
 * distance + duration + ascent/descent metrics carried through for the row's
 * summary and for comparing one whole authoring mode against the other, and the
 * optional `elevation` summary + surface/way-type/slope `analysis` the profile
 * panel renders as its stat strip and its characteristic bands.
 *
 * Both draft flows seed those two blocks from the server's measurement of THIS
 * candidate — previewToDraft for an import, routedSegmentToDraft for a suggest — so
 * a staged draft shows its full profile before anything is created. Nothing
 * recomputes them while a draft is merely staged; the live recompute belongs to an
 * armed geometry edit, which overlays its own reading on top.
 *
 * `ascent`/`descent` are duplicated out of `elevation` deliberately: the editor's
 * live profile only ever measures the candidate currently chosen, so the cost of
 * switching modes cannot be stated without the other candidate's own figure, and
 * they survive on a candidate whose elevation block is incomplete.
 */
export const importCandidateSchema = z.object({
  analysis: liveSegmentRouteAnalysisSchema.nullable().default(null),
  ascent: z.number().nullable().default(null),
  descent: z.number().nullable().default(null),
  distance: z.number().nullable().default(null),
  duration: z.number().nullable().default(null),
  elevation: liveSegmentRouteElevationSchema.nullable().default(null),
  geojson: geometrySchema,
  mode: importSegmentModeSchema,
});

/**
 * ImportCandidate is one authored geometry option for a draft segment, derived
 * from importCandidateSchema.
 */
export type ImportCandidate = z.infer<typeof importCandidateSchema>;

/**
 * itineraryDraftSegmentSchema is the editable shape of one proposed segment: a
 * stable client `id` the editor keys rows + the chosen-mode toggle on, the
 * currently-chosen `mode`, BOTH candidate geometries (`linestring` always
 * present, `directions` absent when the activity cannot route), the display
 * `name` + `color`, and the suggested `speed` in km/h (null = use the activity
 * default). Carrying both candidates lets the mode toggle flip between routed and
 * freehand geometry with no refetch; `draftToView` projects whichever the chosen
 * mode names. The speed is seeded from the preview's per-segment moving speed
 * (null when the source carried no timestamps) and the import speed control edits
 * it; the confirm threads it so the server computes the segment duration from it.
 * Zod-first because the draft is validated at the preview→draft boundary.
 */
export const itineraryDraftSegmentSchema = z.object({
  color: z.string(),
  directions: importCandidateSchema.nullable().default(null),
  id: z.string(),
  linestring: importCandidateSchema,
  mode: importSegmentModeSchema,
  name: z.string(),
  recommendationReason: z.string(),
  /**
   * snapLocked hides the snap/exact mode toggle for this segment: a suggestion is
   * always trail-snapped, so routedSegmentToDraft sets it true and the segment row
   * shows no mode control. An import draft leaves it at the default false, keeping
   * the snap/exact toggle for a segment that carries both candidates.
   */
  snapLocked: z.boolean().default(false),
  speed: z.number().positive().nullable().default(null),
});

/**
 * ItineraryDraftSegment is the editable shape of one proposed segment, derived from
 * itineraryDraftSegmentSchema.
 */
export type ItineraryDraftSegment = z.infer<typeof itineraryDraftSegmentSchema>;

/**
 * itineraryDraftWaypointSchema is the editable shape of one proposed waypoint: a
 * stable client `id`, the wire `mode`, the point `geojson`, the display `name`,
 * and the optional `icon` (the renderer / sprite vocabulary, reusing the shared
 * waypointIconSchema; null when the source symbol could not be mapped). Carried
 * through the draft so the user can edit or drop a waypoint before confirming.
 */
export const itineraryDraftWaypointSchema = z.object({
  geojson: geometrySchema,
  icon: waypointIconSchema.nullable().default(null),
  id: z.string(),
  mode: importWaypointModeSchema,
  name: z.string(),
});

/**
 * ItineraryDraftWaypoint is the editable shape of one proposed waypoint, derived
 * from itineraryDraftWaypointSchema.
 */
export type ItineraryDraftWaypoint = z.infer<typeof itineraryDraftWaypointSchema>;

/**
 * itineraryDraftSchema is the whole editable import draft: the activity scope the
 * preview resolved and the two editable entity lists. It is the working copy the
 * import editor mutates (mode toggles, renames, drops) before the confirm turns
 * it into a persisted itinerary. Validated at the preview→draft boundary so a
 * malformed preview payload is rejected before it reaches the editor.
 */
export const itineraryDraftSchema = z.object({
  activityId: z.string().nullable().default(null),
  segments: z.array(itineraryDraftSegmentSchema),
  waypoints: z.array(itineraryDraftWaypointSchema),
});

/**
 * ItineraryDraft is the whole editable import draft, derived from itineraryDraftSchema.
 */
export type ItineraryDraft = z.infer<typeof itineraryDraftSchema>;

/**
 * importSubmissionStatusSchema is the import flow's progress, a discriminated
 * union on `kind`: `idle` (the shell is open, nothing submitted), `previewing`
 * (the preview request is in flight), `ready` (a preview resolved into a draft
 * the user is editing), `confirming` (the confirm request is in flight), and an
 * error carrying a human-readable `message`. Zod-first because the value drives
 * the shell's button + banner state and is shared with the status-set action.
 *
 * An error may also name a `reason`, for the failure classes the panel answers
 * with advice rather than with the backend's own sentence. `timeout` is the one
 * that exists: the request outlived its budget, so the message that came back is
 * opaque by construction and the useful reply is what would make the work smaller.
 * It stays a small enum owned here rather than the tool bus's ToolErrorCode,
 * because core is a leaf and does not import tools.
 */
export const importSubmissionStatusSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("idle") }),
  z.object({ kind: z.literal("previewing") }),
  z.object({ kind: z.literal("ready") }),
  z.object({ kind: z.literal("confirming") }),
  z.object({
    kind: z.literal("error"),
    message: z.string().min(1),
    reason: z.enum(["timeout"]).optional(),
  }),
]);

/**
 * ImportSubmissionStatus is the import flow's progress state, derived from
 * importSubmissionStatusSchema.
 */
export type ImportSubmissionStatus = z.infer<typeof importSubmissionStatusSchema>;

/**
 * ImportSession is the open import shell state: the chosen provider, the activity
 * the preview resolved (null until a preview lands), the editable itinerary
 * `name`, the owning `organizationId`, the segmentation `granularity`, whether the
 * imported itinerary is `isPrivate`, the transient `file` a file-based provider
 * holds, the `externalId` an online provider holds, the submission `status`, the
 * raw `preview` the server returned, and the editable `draft` projected from it.
 * The `file` is a live File handle — it is validated only as an `instanceof File`
 * and is deliberately kept OUT of any serializable schema (it never round-trips
 * through JSON, a snapshot, or an agent call); every other field is plain data.
 * Mirrors the videoExport slot's "the slice holds the serializable view, the
 * non-serializable handle lives beside it" split.
 */
export interface ImportSession {
  activityId: string | null;
  /**
   * collectionIds is the set of collections the imported itinerary will be added
   * to once it lands. Seeded empty; the shell's collection picker writes the chosen
   * ids and the confirm attaches them best-effort after the create. Like the name +
   * privacy + owner, it does not change what the preview resolves, so writing it
   * never re-previews.
   */
  collectionIds: string[];
  draft: ItineraryDraft | null;
  externalId: string | null;
  file: File | null;
  granularity: ImportGranularity;
  /**
   * isPrivate is whether the confirm persists the imported itinerary as private.
   * Seeded to match the itinerary CREATE flow's default (private) so an import a
   * user never touches the privacy toggle on lands with the same visibility a
   * fresh create would; the shell's privacy Switch flips it and the confirm reads
   * it.
   */
  isPrivate: boolean;
  name: string;
  /**
   * organizationId is the organization that will own the imported itinerary, or
   * null to let the backend default to the authenticated user's organization. The
   * shell's OrganizationSelect seeds it to the user's default writable org and
   * writes the chosen id; the preview + confirm thread it so a cross-org import
   * targets the right owner. Null when the user has no writable organization (the
   * selector renders nothing), in which case neither request sends organization_id.
   */
  organizationId: string | null;
  preview: ItineraryImportPreview | null;
  provider: ImportProviderId;
  status: ImportSubmissionStatus;
}

/**
 * importFileSchema validates the transient upload handle: a real File instance,
 * or null when no file is held (an online provider, or before a pick). It is the
 * ONLY validator the File goes through — the File is never embedded in
 * itineraryDraftSchema or any other serializable schema, so a snapshot or an agent
 * payload cannot smuggle a non-File through. Kept as a standalone schema so the
 * set-file action can assert at its boundary without dragging the File into the
 * draft's shape.
 */
export const importFileSchema = z.instanceof(File).nullable();

/**
 * WireCandidate is one server-measured candidate as the preview carries it. The
 * linestring slot is always present and the directions slot is not, so the
 * projection below is written against the present shape and the caller handles
 * absence.
 */
type WireCandidate = NonNullable<ItineraryImportPreview["segments"][number]["directions"]>;

/**
 * analysisFromPreview projects a candidate's surface/way-type/slope/trail-difficulty
 * stretches into the camelCase block the profile reads. The element shape is already
 * identical on both sides — an inclusive index window and a classified value — so only
 * the outer keys are renamed.
 *
 * The server omits an array it measured nothing for, and a candidate that measured
 * nothing at all gets no block: the four empty arrays a partial projection would
 * produce read as "measured, found nothing", which is a different claim from "not
 * measured" and the one the overlay treats as authoritative.
 */
const analysisFromPreview = (candidate: WireCandidate): LiveSegmentRouteAnalysis | null => {
  if (
    !candidate.slope_info &&
    !candidate.surface_info &&
    !candidate.trail_difficulty_info &&
    !candidate.waytype_info
  ) {
    return null;
  }

  return {
    slopeInfo: candidate.slope_info ?? [],
    surfaceInfo: candidate.surface_info ?? [],
    trailDifficultyInfo: candidate.trail_difficulty_info ?? [],
    waytypeInfo: candidate.waytype_info ?? [],
  };
};

/**
 * elevationFromPreview projects a candidate's elevation summary into the camelCase
 * block the profile's stat strip reads.
 *
 * All six numbers or none. The block is a single measurement of one line, and half of
 * one would let the strip state an altitude range beside an ascent that came from a
 * different reading — so a candidate missing any part of it keeps the neutral nulls
 * and the strip simply omits those cells.
 */
const elevationFromPreview = (candidate: WireCandidate): LiveSegmentRouteElevation | null => {
  const { altitude_max, altitude_min, ascent, descent, gradient_max, gradient_min } = candidate;

  if (
    altitude_max == null ||
    altitude_min == null ||
    ascent == null ||
    descent == null ||
    gradient_max == null ||
    gradient_min == null
  ) {
    return null;
  }

  return {
    altitudeMax: altitude_max,
    altitudeMin: altitude_min,
    ascent,
    descent,
    gradientMax: gradient_max,
    gradientMin: gradient_min,
  };
};

/**
 * candidateFromWire projects one present wire ProposedCandidate into the draft's
 * ImportCandidate: the mode, the geometry, the summary metrics, and the elevation +
 * analysis blocks the profile renders.
 *
 * Those two blocks are carried, not dropped. Nothing recomputes them for a staged
 * draft — the live recompute runs only inside an armed geometry edit — so leaving
 * them null did not defer to a later measurement, it discarded the only one there
 * was, and the panel showed a bare curve with no surface, way-type or difficulty
 * bands and an empty altitude/ascent/descent strip. They ride on BOTH candidates so
 * the profile follows a snap/exact toggle, which is the same reason the scalar
 * ascent/descent are carried on both.
 */
const candidateFromWire = (candidate: WireCandidate): ImportCandidate => ({
  analysis: analysisFromPreview(candidate),
  ascent: candidate.ascent ?? null,
  descent: candidate.descent ?? null,
  distance: candidate.distance ?? null,
  duration: candidate.duration ?? null,
  elevation: elevationFromPreview(candidate),
  geojson: candidate.geojson,
  mode: candidate.mode,
});

/**
 * candidateFromPreview is candidateFromWire for a slot that may be empty. It returns
 * null when the source candidate is absent (a directions candidate the server omitted
 * because the activity cannot route), so the draft segment's `directions` slot stays
 * null and the mode toggle offers only linestring.
 */
const candidateFromPreview = (
  candidate: ItineraryImportPreview["segments"][number]["directions"],
): ImportCandidate | null => (candidate ? candidateFromWire(candidate) : null);

/**
 * previewToDraft projects a wire ItineraryImportPreview into the editable
 * ItineraryDraft the import editor mutates. Each segment seeds its chosen `mode`
 * from the server `recommendation`, carries BOTH candidate geometries (so the
 * mode toggle needs no refetch), and gets a stable client id (its index) the
 * editor keys rows on — the preview has no server ids yet. Its display `name` and
 * `color` are assigned positionally — `Segment #N` (1-based) and the palette
 * swatch at the segment's index — exactly as the persisted editor's add-segment
 * control names and colors a fresh segment, OVERRIDING whatever name/color the
 * preview carried so an imported draft and a hand-built itinerary read the same.
 * Names and colors are seeded positionally once here; any rename or recolor the user
 * makes afterward is preserved, so deleting a mid-list segment can leave the seeded
 * defaults non-contiguous.
 * Each waypoint carries through unchanged with its own client id. The activity is
 * read off the preview's first segment-free top level; a preview always resolves
 * an activity, so it is carried straight through.
 */
export const previewToDraft = (preview: ItineraryImportPreview): ItineraryDraft => ({
  activityId: preview.activity_id ?? null,
  segments: preview.segments.map((segment, index) => ({
    // The display name + color are positional, matching the persisted editor's
    // add-segment flow: defaultSegmentName / segmentColorAt by array index. They
    // override the preview's own name/color so the source order reads as
    // "Segment #1", "Segment #2", … against an even spread of palette hues.
    color: segmentColorAt(index),
    directions: candidateFromPreview(segment.directions),
    id: String(index),
    // linestring is always present on the wire; project it through the same
    // projection as the directions slot so a directions-less segment still has a
    // geometry to render, and so the two candidates can never come to describe
    // themselves differently.
    linestring: candidateFromWire(segment.linestring),
    // Seed the chosen mode from the server recommendation — the user can override
    // it in the editor, but the draft opens on what the server suggests.
    mode: segment.recommendation,
    name: defaultSegmentName(index),
    recommendationReason: segment.recommendation_reason,
    // An import segment is never snap-locked, so the row keeps its snap/exact
    // toggle (a suggestion sets this true to hide the toggle).
    snapLocked: false,
    // Seed the suggested moving speed (km/h) the server derived from the source's
    // per-point timing; null when the source carried no timestamps, in which case
    // the segment falls back to the activity default until the user sets one.
    speed: segment.speed ?? null,
  })),
  waypoints: preview.waypoints.map((waypoint, index) => ({
    geojson: waypoint.geojson,
    icon: waypoint.icon ?? null,
    id: String(index),
    mode: waypoint.mode,
    name: waypoint.name ?? "",
  })),
});

/**
 * RoutedSegmentDraftParams is the input to {@link routedSegmentToDraft}: a chosen
 * route already resolved into the vias the router was asked to route through, the
 * routed `routedGeometry` LineString the backend echoed, its `distanceMeters` and
 * `durationSeconds` metrics, and the display `name` + `color` the segment carries.
 * The `activityId` scopes the draft so a later geometry edit can resolve the
 * activity to re-route under (the draft sink reads it). The caller routes first,
 * then hands the result here — the builder is pure and does no I/O.
 */
export interface RoutedSegmentDraftParams {
  activityId: string | null;
  /**
   * analysis is the routed surface/way-type/slope analysis the started-from
   * segment carries — already converted to the persisted snake_case + REST shape.
   * Null when the route carried none, in which case the draft segment's profile
   * shows no analysis bands until a re-edit routes one.
   */
  analysis: LiveSegmentRouteAnalysis | null;
  color: string;
  distanceMeters: number | null;
  durationSeconds: number | null;
  /**
   * elevation is the routed elevation summary the started-from segment carries,
   * so the draft's profile shows the altitude/gradient characteristics
   * immediately. Null when the route carried no elevation (a 2D-only response).
   */
  elevation: LiveSegmentRouteElevation | null;
  name: string;
  routedGeometry: LineString;
  vias: Position[];
}

/**
 * routedSegmentToDraft builds an ItineraryDraft carrying a single DIRECTIONS
 * segment from an already-routed segment, shaped EXACTLY like the directions
 * segment previewToDraft produces so the suggest draft edits + commits through the
 * identical code paths an imported directions draft does. The directions candidate
 * geometry is the single-level GeometryCollection the wire contract uses
 * ({@link buildGeometryCollection}) carrying BOTH the routed LineString (which
 * extractLineStrings reads for rendering + the elevation profile) AND a MultiPoint
 * of the vias (which extractVias reads to seed the segment-edit engine on re-edit),
 * matching the directions candidate previewToDraft carries off the wire. The
 * always-present linestring candidate wraps the same routed line so a toggle to
 * freehand still renders a geometry. The chosen `mode` is directions, the id is the
 * first-segment id ("0") the editor keys rows on, and the recommendation reason is
 * empty (a routed segment carries none). Pure — it reads no store and calls no API,
 * so the commit tool routes first and then builds the draft deterministically.
 */
export const routedSegmentToDraft = (params: RoutedSegmentDraftParams): ItineraryDraft => {
  // The vias MultiPoint rides in the directions candidate's collection so a
  // re-edit's extractVias reads the same seed points the route was built from;
  // the routed LineString rides alongside it for rendering + the elevation panel,
  // matching the [routedLine, vias] shape a preview's directions candidate carries.
  const viasGeometry: MultiPoint = { coordinates: params.vias, type: "MultiPoint" };
  const directionsGeometry = buildGeometryCollection([params.routedGeometry, viasGeometry]);
  const linestringGeometry = buildGeometryCollection([params.routedGeometry]);

  return {
    activityId: params.activityId,
    segments: [
      {
        color: params.color,
        directions: {
          // The routed elevation + analysis ride on the directions candidate so
          // the started-from draft's profile shows its full characteristics
          // (chart + the surface/way-type/slope bars) before any re-edit. The
          // scalar ascent/descent come off that same block rather than being
          // measured twice.
          analysis: params.analysis,
          ascent: params.elevation?.ascent ?? null,
          descent: params.elevation?.descent ?? null,
          distance: params.distanceMeters,
          duration: params.durationSeconds,
          elevation: params.elevation,
          geojson: directionsGeometry,
          mode: "directions",
        },
        id: "0",
        linestring: {
          // The linestring candidate wraps the SAME routed line, so it carries
          // the same elevation + analysis for a toggle-to-freehand to keep the
          // profile.
          analysis: params.analysis,
          ascent: params.elevation?.ascent ?? null,
          descent: params.elevation?.descent ?? null,
          distance: params.distanceMeters,
          duration: params.durationSeconds,
          elevation: params.elevation,
          geojson: linestringGeometry,
          mode: "linestring",
        },
        mode: "directions",
        name: params.name,
        recommendationReason: "",
        snapLocked: true,
        speed: null,
      },
    ],
    waypoints: [],
  };
};

/**
 * chosenCandidate returns the candidate geometry a draft segment's chosen mode
 * names: the directions candidate when the segment is in directions mode AND that
 * candidate exists, otherwise the always-present linestring. The fallback keeps
 * the projection total — a segment left (or seeded) in directions mode with no
 * directions candidate still renders its freehand line rather than vanishing.
 */
const chosenCandidate = (segment: ItineraryDraftSegment): ImportCandidate =>
  segment.mode === "directions" && segment.directions ? segment.directions : segment.linestring;

/**
 * draftSegmentToItinerary projects one draft segment into the persisted
 * ItinerarySegment shape the editor's segment list + elevation panel read. The
 * geometry is the chosen candidate's; the id, name, color, and mode carry
 * through. The altitude/gradient fields + the surface/way-type/slope analysis are
 * overlaid from the CHOSEN candidate's elevation + analysis, which is what makes the
 * profile follow a snap/exact toggle rather than describing whichever candidate
 * happened to be seeded. A candidate the server measured nothing for keeps the
 * neutral nulls and the empty analysis arrays the API boundary normalizes an absent
 * analysis to — the same stance synthesizeDraftSegment takes for a from-scratch edit —
 * and the panel's bands and stat cells drop out rather than showing zeroes.
 * The itinerary scope is empty (a preview has no itinerary id), which the
 * `preview`-mode view tolerates, and the group is null — groups are display metadata
 * over a persisted chain, so a draft segment belongs to none.
 */
const draftSegmentToItinerary = (segment: ItineraryDraftSegment): ItinerarySegment => {
  const candidate = chosenCandidate(segment);
  const base: ItinerarySegment = {
    altitude_max: null,
    altitude_min: null,
    ascent: null,
    color: segment.color,
    created_at: new Date(0),
    created_by: { id: "" },
    descent: null,
    description: null,
    distance: candidate.distance,
    duration: candidate.duration,
    geojson: candidate.geojson,
    gradient_max: null,
    gradient_min: null,
    group_id: null,
    id: segment.id,
    itinerary_id: "",
    mode: segment.mode,
    name: segment.name,
    position: 0,
    revision: 0,
    slope_info: [],
    speed: segment.speed,
    start_at: null,
    steps: [],
    surface_info: [],
    trail_difficulty_info: [],
    waytype_info: [],
  };

  // Overlay the server's elevation + analysis for this candidate; a null block
  // leaves the neutral seed untouched, so a candidate nothing was measured for
  // renders as a bare curve rather than as a flat one.
  return overlayRouteAnalysis(base, {
    analysis: candidate.analysis ?? undefined,
    elevation: candidate.elevation ?? undefined,
  });
};

/**
 * draftWaypointToItinerary projects one draft waypoint into the persisted
 * ItineraryWaypoint shape. The geometry, mode, name, and icon carry through; the
 * position is the array index (waypoints are ordered by their draft order); the
 * remaining required fields are the neutral seed until a confirm persists the
 * real entity. Color defaults to null so the marker falls back to the activity
 * tint, matching the persisted contract.
 */
const draftWaypointToItinerary = (
  waypoint: ItineraryDraftWaypoint,
  position: number,
): ItineraryWaypoint => ({
  color: null,
  created_at: new Date(0),
  created_by: { id: "" },
  description: null,
  geojson: waypoint.geojson,
  icon: waypoint.icon,
  id: waypoint.id,
  itinerary_id: "",
  mode: waypoint.mode,
  name: waypoint.name,
  position,
  revision: 0,
});

/**
 * draftToView projects an ItineraryDraft into the ItineraryViewSource render-model
 * the studio editor consumes — the same shape a persisted itinerary narrows to,
 * so the segment list, waypoint list, and elevation panel render an import
 * preview through one path. Each segment surfaces with its CHOSEN candidate's
 * geometry (the mode toggle's effect is visible immediately), each waypoint with
 * its current attributes. The view's id is null (a preview has no server id) and
 * the activity carries through from the draft.
 */
export const draftToView = (draft: ItineraryDraft): ItineraryViewSource => ({
  activity_id: draft.activityId,
  id: null,
  segments: draft.segments.map(draftSegmentToItinerary),
  waypoints: draft.waypoints.map(draftWaypointToItinerary),
});

/**
 * ConfirmContext is the small bag of confirm-only fields draftToConfirmParams
 * folds into the draft — the user-typed `name`, the `isPrivate` flag, the resolved
 * `activityId`, and the optional owning `organizationId`. These live in the import
 * session beside the draft (the draft itself models only the editable entities),
 * so the adapter takes them as an explicit argument rather than reading them back
 * off the draft. `organizationId` is null/absent when the user has no writable
 * organization, in which case the confirm omits `organization_id` and the backend
 * defaults to the auth org.
 */
export interface ConfirmContext {
  activityId: string;
  isPrivate: boolean;
  name: string;
  organizationId?: string | null;
}

/**
 * draftSegmentToConfirm projects one draft segment into the wire confirm shape:
 * the CHOSEN candidate's mode + defining geometry, the segment name, its color
 * (an empty draft color becomes null so the server falls back to the activity
 * tint, matching the confirm contract), and the suggested `speed` in km/h (null =
 * the server uses the activity default). Every OTHER metric is recomputed
 * server-side from the geometry at confirm time, so none is sent; the speed is the
 * one input the server consumes to compute the segment's duration.
 */
const draftSegmentToConfirm = (segment: ItineraryDraftSegment): ItineraryImportConfirmSegment => {
  const candidate = chosenCandidate(segment);

  return {
    color: segment.color === "" ? null : segment.color,
    geojson: candidate.geojson,
    mode: candidate.mode,
    name: segment.name,
    speed: segment.speed,
  };
};

/**
 * draftWaypointToConfirm projects one draft waypoint into the wire confirm shape:
 * its mode, point geometry, name, and icon. The draft carries no free-text
 * description (the preview surfaces none into the editable shape), so description
 * is sent null. An empty name becomes null so the server applies its own
 * fallback rather than persisting a blank label.
 */
const draftWaypointToConfirm = (
  waypoint: ItineraryDraftWaypoint,
): ItineraryImportConfirmWaypoint => ({
  description: null,
  geojson: waypoint.geojson,
  icon: waypoint.icon,
  mode: waypoint.mode,
  name: waypoint.name === "" ? null : waypoint.name,
});

/**
 * draftToConfirmParams projects an ItineraryDraft plus the confirm-only context
 * (name, privacy, activity, owning organization) into the wire
 * ItineraryImportConfirmParams the confirm endpoint persists. Each segment
 * surfaces with its CHOSEN candidate's mode + geometry (the mode toggle's effect
 * is what gets written), each waypoint with its current attributes; metrics are
 * recomputed server-side, so only the defining geometry rides through. The
 * `organization_id` is set only when the context carries one — a null/absent owner
 * leaves the field off so the backend defaults to the auth org. Pure — it reads no
 * store, so the confirm tool can build the request body deterministically from the
 * draft it holds.
 */
export const draftToConfirmParams = (
  draft: ItineraryDraft,
  context: ConfirmContext,
): ItineraryImportConfirmParams => ({
  activity_id: context.activityId,
  is_private: context.isPrivate,
  name: context.name,
  // Omit organization_id when the context has none so the backend defaults to the
  // authenticated user's org; set it to the chosen id otherwise.
  ...(context.organizationId ? { organization_id: context.organizationId } : {}),
  segments: draft.segments.map(draftSegmentToConfirm),
  waypoints: draft.waypoints.map(draftWaypointToConfirm),
});
